import { CSSProperties, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Play } from '@sorare/core/src/atoms/icons/Play';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BodyL } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog/Dialog';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  competitionsVideo,
  getStartedVideo,
  introductionVideo,
  marketVideo,
  scoringVideo,
} from './data';

const Root = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
`;
const VideoWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 100%;
  height: 100%;
  position: relative;
  isolation: isolate;

  @media ${laptopAndAbove} {
    height: auto;
    aspect-ratio: calc(16 / 9);
  }
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  padding: var(--double-unit);
`;
const VideoPreviewButton = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;
const MarketVideoPreviewButton = styled(VideoPreviewButton)`
  border-radius: var(--double-unit);
  width: 100%;
  min-height: calc(44 * var(--unit));
`;
const MarketVideoPreviewBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    linear-gradient(to top right, var(--c-nl-500), var(--c-nl-800)),
    center / cover no-repeat url(${marketVideo.en.thumbnail});
  mix-blend-mode: luminosity;
`;
const SecondaryVideoPreviewButton = styled(VideoPreviewButton)`
  border-radius: var(--intermediate-unit);
  width: 100%;
  min-height: calc(36 * var(--unit));
`;
const SecondaryVideoPreviewBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    linear-gradient(to top right, var(--c-nl-500), var(--c-nl-500)),
    center / cover no-repeat var(--url);
`;

const secondaryVideos = [
  introductionVideo,
  getStartedVideo,
  scoringVideo,
  competitionsVideo,
];

type Props = { open: boolean; onClose: () => void };
export const MarketVideoDialog = ({ open, onClose }: Props) => {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  const onCloseVideo = () => {
    setSelectedVideo(null);
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <Root>
        <MarketVideoPreviewButton
          onClick={() => {
            setSelectedVideo(marketVideo.en.src);
          }}
        >
          <MarketVideoPreviewBackground />
          <IconButton asSpan color="secondary">
            <Play />
          </IconButton>
        </MarketVideoPreviewButton>
        <Vertical gap={2}>
          <Vertical gap={0}>
            <BodyL bold>
              <FormattedMessage {...glossary.continue} />
            </BodyL>
            <BodyL color="var(--c-nd-700)">
              <FormattedMessage
                id="MarketVideoDialog.subtitle"
                defaultMessage="Master the basics"
              />
            </BodyL>
          </Vertical>
          <Scrollable itemToDisplay={2} overhang={4}>
            {secondaryVideos.map(({ key, en }) => {
              return (
                <SecondaryVideoPreviewButton
                  key={key}
                  onClick={() => {
                    setSelectedVideo(en.src);
                  }}
                >
                  <SecondaryVideoPreviewBackground
                    style={
                      {
                        '--url': `url(${en.thumbnail})`,
                      } as CSSProperties
                    }
                  />
                  <IconButton asSpan color="secondary">
                    <Play />
                  </IconButton>
                </SecondaryVideoPreviewButton>
              );
            })}
          </Scrollable>
        </Vertical>
      </Root>
      {selectedVideo && (
        <Dialog
          onClose={onCloseVideo}
          open={!!selectedVideo}
          maxWidth="md"
          fullWidth
          hideHeader
        >
          {({ CloseButton }) => (
            <VideoWrapper>
              <CloseButtonWrapper>
                <CloseButton onClose={onCloseVideo} />
              </CloseButtonWrapper>
              <iframe
                width="100%"
                height="100%"
                src={selectedVideo}
                title="YouTube video player"
                style={{ border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </VideoWrapper>
          )}
        </Dialog>
      )}
    </Dialog>
  );
};
